import React, { useState, useEffect, useRef } from 'react';
import { Clock, AlertCircle } from 'lucide-react';

interface TestTimerProps {
  totalSeconds: number;
  onTimeUp?: () => void;
  onTimeUpdate?: (remainingSeconds: number) => void;
  isPaused?: boolean;
  className?: string;
  // Session timing information
  sessionInfo?: {
    sessionCount: number;
    previousSessionsTime: number;
    currentSessionStart: number;
  };
}

export const TestTimer: React.FC<TestTimerProps> = ({
  totalSeconds,
  onTimeUp,
  onTimeUpdate,
  isPaused = false,
  className = '',
  sessionInfo
}) => {
  // Calculate initial remaining time considering previous sessions
  const calculateInitialTime = () => {
    if (!sessionInfo) return totalSeconds;
    
    const currentSessionElapsed = Math.floor((Date.now() - sessionInfo.currentSessionStart) / 1000);
    const totalElapsed = sessionInfo.previousSessionsTime + currentSessionElapsed;
    return Math.max(0, totalSeconds - totalElapsed);
  };
  
  const [remainingTime, setRemainingTime] = useState(calculateInitialTime());
  const [isWarning, setIsWarning] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [showWarningModal, setShowWarningModal] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout>();
  const hasCalledTimeUp = useRef(false);
  const hasShownFiveMinWarning = useRef(false);
  const hasShownOneMinWarning = useRef(false);

  useEffect(() => {
    if (isPaused) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemainingTime(prev => {
        const newTime = Math.max(0, prev - 1);
        
        // Schedule onTimeUpdate callback to avoid setState during render
        if (onTimeUpdate) {
          setTimeout(() => onTimeUpdate(newTime), 0);
        }
        
        // Check warning states
        const percentRemaining = (newTime / totalSeconds) * 100;
        if (percentRemaining <= 10) {
          setIsCritical(true);
          setIsWarning(false);
        } else if (percentRemaining <= 25) {
          setIsWarning(true);
          setIsCritical(false);
        }
        
        // Show 5 minute warning
        if (newTime === 300 && !hasShownFiveMinWarning.current) {
          hasShownFiveMinWarning.current = true;
          setShowWarningModal(true);
          setTimeout(() => setShowWarningModal(false), 3000);
        }
        
        // Show 1 minute warning
        if (newTime === 60 && !hasShownOneMinWarning.current) {
          hasShownOneMinWarning.current = true;
          setShowWarningModal(true);
          setTimeout(() => setShowWarningModal(false), 3000);
        }
        
        // Handle time up
        if (newTime === 0 && !hasCalledTimeUp.current) {
          hasCalledTimeUp.current = true;
          if (onTimeUp) {
            onTimeUp();
          }
        }
        
        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPaused, totalSeconds, onTimeUp, onTimeUpdate]);

  const formatTime = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (isCritical) return 'text-red-600 bg-red-50 border-red-200';
    if (isWarning) return 'text-orange-600 bg-orange-50 border-orange-200';
    return 'text-gray-700 bg-white border-gray-200';
  };

  const getProgressBarColor = () => {
    if (isCritical) return 'bg-red-500';
    if (isWarning) return 'bg-orange-500';
    return 'bg-blue-500';
  };

  const progressPercent = (remainingTime / totalSeconds) * 100;

  return (
    <>
      {/* Warning Modal */}
      {showWarningModal && (
        <div style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: remainingTime <= 60 ? '#dc2626' : '#ea580c',
          color: 'white',
          padding: '20px 40px',
          borderRadius: '12px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
          zIndex: 9999,
          fontSize: '24px',
          fontWeight: 'bold',
          textAlign: 'center',
          animation: 'pulse 1s ease-in-out infinite'
        }}>
          <div>⚠️ DİKKAT! ⚠️</div>
          <div style={{ marginTop: '10px' }}>
            {remainingTime <= 60 ? 'SON 1 DAKİKA!' : 'SON 5 DAKİKA!'}
          </div>
        </div>
      )}
      
      <div className={`test-timer ${className}`}>
      <div className={`
        flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all duration-300
        ${getTimerColor()}
      `}>
        {isCritical ? (
          <AlertCircle className="w-5 h-5 animate-pulse" />
        ) : (
          <Clock className="w-5 h-5" />
        )}
        <div className="flex flex-col">
          <span className="text-xs font-medium opacity-70">
            {isCritical ? 'Süre Bitiyor!' : isWarning ? 'Dikkat!' : 'Kalan Süre'}
          </span>
          <span className="text-xl font-bold font-mono">
            {formatTime(remainingTime)}
          </span>
        </div>
      </div>
      
      {/* Progress bar */}
      <div className="w-full h-1 bg-gray-200 rounded-full mt-2 overflow-hidden">
        <div 
          className={`h-full transition-all duration-1000 ${getProgressBarColor()}`}
          style={{ width: `${progressPercent}%` }}
        />
      </div>
      
      {/* Session info */}
      {sessionInfo && sessionInfo.sessionCount > 1 && (
        <div className="text-xs text-gray-500 mt-1 text-center">
          {sessionInfo.sessionCount}. oturum
        </div>
      )}
    </div>
    </>
  );
};

// Mobile version with compact design
export const MobileTestTimer: React.FC<TestTimerProps> = (props) => {
  // Calculate initial remaining time considering previous sessions
  const calculateInitialTime = () => {
    if (!props.sessionInfo) return props.totalSeconds;
    
    const currentSessionElapsed = Math.floor((Date.now() - props.sessionInfo.currentSessionStart) / 1000);
    const totalElapsed = props.sessionInfo.previousSessionsTime + currentSessionElapsed;
    return Math.max(0, props.totalSeconds - totalElapsed);
  };
  
  const [remainingTime, setRemainingTime] = useState(calculateInitialTime());
  const intervalRef = useRef<NodeJS.Timeout>();
  const hasCalledTimeUp = useRef(false);

  useEffect(() => {
    if (props.isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setRemainingTime(prev => {
        const newTime = Math.max(0, prev - 1);
        if (props.onTimeUpdate) setTimeout(() => props.onTimeUpdate?.(newTime), 0);
        if (newTime === 0 && !hasCalledTimeUp.current) {
          hasCalledTimeUp.current = true;
          if (props.onTimeUp) props.onTimeUp();
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [props.isPaused, props.totalSeconds, props.onTimeUp, props.onTimeUpdate]);

  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const isCritical = remainingTime <= props.totalSeconds * 0.1;
  const isWarning = remainingTime <= props.totalSeconds * 0.25;

  return (
    <div className={`
      flex items-center gap-1 h-full text-xs
      ${isCritical ? 'text-red-600' : isWarning ? 'text-orange-600' : 'text-gray-700'}
      ${props.className}
    `}>
      <Clock className="w-3 h-3" />
      <span className="font-mono font-medium">{formatTime(remainingTime)}</span>
      {props.sessionInfo && props.sessionInfo.sessionCount > 1 && (
        <span className="text-[10px] opacity-70">({props.sessionInfo.sessionCount})</span>
      )}
    </div>
  );
};